import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, message, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { Reservation, ReservationType, ReservationStatus } from '@/types/residents/reservation';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { VendorSelector } from '@/components/common/VendorSelector';
import { AmenitySelector } from '@/components/common/AmenitySelector';
import { formatFirestoreTimestamp } from '@/utils/date';


const { TextArea } = Input;

type ReservedByType = 'resident' | 'employee' | 'vendor';

interface ReservationModalProps {
  open: boolean;
  reservation?: Reservation | null;
  onSuccess: (data: Omit<Reservation, 'id'>) => void;
  onCancel: () => void;
}

interface FormValues {
  amenityId: string;
  type: ReservationType;
  status: ReservationStatus;
  reservedBy: ReservedByType;
  residentId?: string;
  employeeId?: string;
  vendorId?: string;
  startTime: Dayjs;
  endTime: Dayjs;
  attendees?: number;
  purpose: string;
  notes?: string;
  receivableId?: string;
}

const ReservationModal: React.FC<ReservationModalProps> = ({
  open,
  reservation,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['reservations', 'common']);
  const [form] = Form.useForm<FormValues>();
  const [selectedAmenityData, setSelectedAmenityData] = useState<{ id: string; name: string } | null>(null);
  const [reservedBy, setReservedBy] = useState<ReservedByType>('resident');
  const [selectedResident, setSelectedResident] = useState<{ id: string; name: string } | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; name: string } | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (open && reservation) {
      // Determine reserved by type based on existing data
      let reservedByType: ReservedByType = 'resident';
      if (reservation.employee) {
        reservedByType = 'employee';
      } else if (reservation.vendor) {
        reservedByType = 'vendor';
      }

      form.setFieldsValue({
        ...reservation,
        startTime: reservation.startTime ? dayjs(formatFirestoreTimestamp(reservation.startTime)) : undefined,
        endTime: reservation.endTime ? dayjs(formatFirestoreTimestamp(reservation.endTime)) : undefined,
        amenityId: reservation.amenity?.id,
        reservedBy: reservedByType,
        residentId: reservation.resident?.id,
        employeeId: reservation.employee?.id,
        vendorId: reservation.vendor?.id,
        receivableId: reservation.receivable?.id,
      });
      setSelectedAmenityData(reservation.amenity || null);
      setReservedBy(reservedByType);
      
      // Set the selected data
      if (reservation.resident) {
        setSelectedResident(reservation.resident);
      }
      if (reservation.employee) {
        setSelectedEmployee(reservation.employee);
      }
      if (reservation.vendor) {
        setSelectedVendor(reservation.vendor);
      }
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        startTime: dayjs(),
        endTime: dayjs().add(1, 'hour'),
        status: 'pending',
        type: 'personal',
        reservedBy: 'resident',
        attendees: 1,
      });
      setSelectedAmenityData(null);
      setReservedBy('resident');
      setSelectedResident(null);
      setSelectedEmployee(null);
      setSelectedVendor(null);
    }
  }, [open, reservation, form]);

  const handleReservedByChange = (value: ReservedByType) => {
    setReservedBy(value);
    // Clear the other fields when switching
    form.setFieldsValue({
      residentId: undefined,
      employeeId: undefined,
      vendorId: undefined,
    });
    setSelectedResident(null);
    setSelectedEmployee(null);
    setSelectedVendor(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const data: Omit<Reservation, 'id'> = {
        amenity: selectedAmenityData || { id: values.amenityId, name: '' },
        type: values.type,
        status: values.status,
        startTime: values.startTime.toDate(),
        endTime: values.endTime.toDate(),
        attendees: values.attendees,
        purpose: values.purpose,
        notes: values.notes,
      };

      // Only add fields that are not undefined
      if (values.reservedBy === 'resident' && values.residentId && selectedResident) {
        data.resident = selectedResident;
      }
      
      if (values.reservedBy === 'employee' && values.employeeId && selectedEmployee) {
        data.employee = selectedEmployee;
      }
      
      if (values.reservedBy === 'vendor' && values.vendorId && selectedVendor) {
        data.vendor = selectedVendor;
      }
      
      if (values.receivableId) {
        data.receivable = { id: values.receivableId, name: '' };
      }

      onSuccess(data);
    } catch (error) {
      message.error(t('common:error'));
    }
  };

  const renderReservedBySelector = () => {
    switch (reservedBy) {
      case 'resident':
        return (
          <Form.Item
            name="residentId"
            label={t('reservations:resident')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <ResidentSelector onResidentDataChange={setSelectedResident} />
          </Form.Item>
        );
      case 'employee':
        return (
          <Form.Item
            name="employeeId"
            label={t('reservations:employee')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <EmployeeSelector onEmployeeDataChange={setSelectedEmployee} />
          </Form.Item>
        );
      case 'vendor':
        return (
          <Form.Item
            name="vendorId"
            label={t('reservations:vendor')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <VendorSelector onVendorDataChange={setSelectedVendor} />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title={reservation ? t('reservations:editReservation') : t('reservations:addReservation')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={reservation ? t('common:actions.update') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      destroyOnHidden
      width={800}
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <AmenitySelector onAmenityDataChange={setSelectedAmenityData} />
          </Col>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('reservations:type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                            <Select.Option value="personal">{t('reservations:types.personal')}</Select.Option>
            <Select.Option value="community">{t('reservations:types.community')}</Select.Option>
            <Select.Option value="commercial">{t('reservations:types.commercial')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('reservations:status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                            <Select.Option value="pending">{t('reservations:statuses.pending')}</Select.Option>
            <Select.Option value="approved">{t('reservations:statuses.approved')}</Select.Option>
            <Select.Option value="rejected">{t('reservations:statuses.rejected')}</Select.Option>
            <Select.Option value="cancelled">{t('reservations:statuses.cancelled')}</Select.Option>
            <Select.Option value="completed">{t('reservations:statuses.completed')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="attendees"
              label={t('reservations:attendees')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="reservedBy"
              label={t('reservations:reservedBy')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select onChange={handleReservedByChange}>
                            <Select.Option value="resident">{t('reservations:reservedByOptions.resident')}</Select.Option>
            <Select.Option value="employee">{t('reservations:reservedByOptions.employee')}</Select.Option>
            <Select.Option value="vendor">{t('reservations:reservedByOptions.vendor')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {renderReservedBySelector()}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="startTime"
              label={t('reservations:startTime')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="endTime"
              label={t('reservations:endTime')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="purpose"
          label={t('reservations:purpose')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea rows={3} placeholder={t('reservations:purposePlaceholder')} />
        </Form.Item>

        <Form.Item
          name="notes"
          label={t('reservations:notes')}
        >
          <TextArea rows={3} placeholder={t('reservations:notesPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default ReservationModal; 